/**
 * ╔═══════════════════════════════════════════════════════════════════════════════╗
 * ║  OrgBoardNodeComponent — Unified ECO Tree Node Component                    ║
 * ╠═══════════════════════════════════════════════════════════════════════════════╣
 * ║                                                                             ║
 * ║  PURPOSE:                                                                   ║
 * ║  Renders a single node (card) on the Organization Board tree.               ║
 * ║  This is the SINGLE source of truth for all org board node rendering,       ║
 * ║  replacing the three previous components that were ~90% identical:          ║
 * ║    - NoUserComponent   (shared-no-user)    — 0 users assigned              ║
 * ║    - OneUserComponent  (shared-one-user)   — 1 user assigned               ║
 * ║    - ManyUsersComponent (shared-many-users) — 2+ users assigned            ║
 * ║                                                                             ║
 * ║  HOW IT WORKS:                                                              ║
 * ║  The parent passes [userDisplayMode] which controls ONLY the               ║
 * ║  ECO-level user display at the top of each card (Section A in the HTML).    ║
 * ║  Everything else — sections, units, posts, VFP, HFA — is identical         ║
 * ║  regardless of user count.                                                  ║
 * ║                                                                             ║
 * ║  BUSINESS RULES:                                                            ║
 * ║  • Only "post" parts can be directly staffed with users                    ║
 * ║  • Containers (executive, division, department, section, unit) NEVER       ║
 * ║    receive users directly — they render Posts inline which hold users       ║
 * ║  • HFA (Held From Above) only applies to Posts with no own users           ║
 * ║  • VFP (Valuable Final Product) applies to all part types                  ║
 * ║                                                                             ║
 * ║  TEMPLATE STRUCTURE (see org-board-node.component.html):                   ║
 * ║    A. ECO-Level User Display (varies by userDisplayMode)                   ║
 * ║    B. Editing User Dropzone (CDK drag-drop)                                ║
 * ║    C. Posts In Parent (postsInParent)                                       ║
 * ║    D. Sections In Department → Units → Posts (nested hierarchy)            ║
 * ║    E. Node-Level VFP                                                        ║
 * ║                                                                             ║
 * ║  HEIGHT CALCULATION DEPENDENCY:                                             ║
 * ║  If you add/remove/resize template elements, update the PX_* constants     ║
 * ║  in organization-board.component.ts → updateNodeDimensions().              ║
 * ║  See: docs/ORG-BOARD-HEIGHT-CALCULATION-SPEC.md                            ║
 * ║                                                                             ║
 * ╚═══════════════════════════════════════════════════════════════════════════════╝
 */

import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { OrbBoardBaseComponent } from '../../org-board-base-component';
import { Position } from '../../services/org-board-builder.service';
import { ProfileColorService } from '../../services/profile-color.service';
import { ECONode, Organization, OrgUser } from '@newgenus/common';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { CdkDropList } from '@angular/cdk/drag-drop';

/** The three possible user display modes for the ECO-level card header */
export type UserDisplayMode = 'NO_USERS' | 'ONE_USER' | 'MULTIPLE_USERS';

@Component({
    selector: 'shared-org-board-node',
    imports: [CommonModule, MaterialModule, TitleCasePipe],
    templateUrl: './org-board-node.component.html',
    styleUrls: ['../../organization-board.component.scss'],
})
export class OrgBoardNodeComponent extends OrbBoardBaseComponent {
    // ──────────────────────────────────────────────────────────────
    //  Injected Services
    // ──────────────────────────────────────────────────────────────

    private profileColorService = inject(ProfileColorService);
    private elRef = inject(ElementRef);

    // ──────────────────────────────────────────────────────────────
    //  Inputs
    // ──────────────────────────────────────────────────────────────

    /**
     * Controls the ECO-level user display at the top of the card.
     * This is the ONLY visual difference between the old 3 components:
     *   'NO_USERS'       → "Position is currently unoccupied"
     *   'ONE_USER'        → Single avatar + name
     *   'MULTIPLE_USERS'  → First avatar + "and X other(s)"
     */
    @Input() userDisplayMode: UserDisplayMode = 'NO_USERS';

    /** Whether drag-based navigation is disabled */
    @Input() isDragNavDisabled = false;

    /** Whether the user is currently drag-navigating the board */
    @Input() isNavigatingWithDrag = false;

    /** Whether a droppable item (user) is currently being dragged */
    @Input() isDroppableItemBeingDragged = false;

    /** Whether the board is in edit mode (enables menus, dropzones, VFP editing) */
    @Input() isEditing = false;

    /** Whether VFP editing toggle is on (controls VFP hover reveal on org parts) */
    @Input() isVfpEditingOn = false;

    /** Board-level "Hide staff" (edit mode): the visuals are CSS on the board root; this input
     *  withdraws the staff MENU items, which render in CDK overlays that CSS cannot reach. */
    @Input() hideStaff = false;

    /** Set of partKeys currently being deleted — used to grey out and disable individual parts */
    @Input() deletingPartKeys: Set<string> = new Set();

    /** Set of userId:partKey composite keys being processed — used to grey out specific user rows */
    @Input() processingUserKeys: Set<string> = new Set();

    /** The ECO tree node data to render */
    @Input() node!: ECONode<Position>;

    /** Connected CDK drop lists for drag-drop operations */
    @Input() dropLists!: CdkDropList[];

    /** User data for the CDK drop list */
    @Input() data: any[] = [];

    // ──────────────────────────────────────────────────────────────
    //  Outputs — Node-level events
    // ──────────────────────────────────────────────────────────────

    /** Emitted when the user clicks the card to select/navigate to this node */
    @Output() selectSiblingNode = new EventEmitter<ECONode<Position>>();

    /** Emitted when a user avatar is clicked */
    @Output() openUser = new EventEmitter<OrgUser>();

    /** Emitted to open the sidebar template for this node */
    @Output() openTemplate = new EventEmitter<any>();

    /** Emitted when a user name/avatar is clicked — opens user details sidebar */
    @Output() openUserTemplate = new EventEmitter<{ user: any; node: ECONode<Position> }>();

    /** Emitted when drag state changes (true = dragging, false = not dragging) */
    @Output() stateChange = new EventEmitter<boolean>();

    /** Emitted when a drag operation ends on this node */
    @Output() dragEnded = new EventEmitter<ECONode<Position>>();

    /** Emitted when a drag operation starts on this node */
    @Output() dragStarted = new EventEmitter<ECONode<Position>>();

    /** Emitted when a user is dropped onto this node's drop zone */
    @Output() templateDrop = new EventEmitter<{ event: any; node?: ECONode<Position> }>();

    /** Emitted when the CDK drop list is initialized — parent uses this to track drop targets */
    @Output() dropList = new EventEmitter<CdkDropList>();

    // ──────────────────────────────────────────────────────────────
    //  Outputs — Section actions
    // ──────────────────────────────────────────────────────────────

    @Output() editSection = new EventEmitter<any>();
    @Output() addSiblingSection = new EventEmitter<any>();
    @Output() addChildUnit = new EventEmitter<any>();
    @Output() deleteSection = new EventEmitter<any>();
    @Output() reorderSectionChildren = new EventEmitter<any>();

    // ──────────────────────────────────────────────────────────────
    //  Outputs — Unit actions
    // ──────────────────────────────────────────────────────────────

    @Output() addSiblingUnit = new EventEmitter<unknown>();
    @Output() addChildPost = new EventEmitter<unknown>();
    @Output() editUnit = new EventEmitter<unknown>();
    @Output() deleteUnit = new EventEmitter<unknown>();
    @Output() reorderUnitChildren = new EventEmitter<any>();

    // ──────────────────────────────────────────────────────────────
    //  Outputs — Bulk assign
    // ──────────────────────────────────────────────────────────────

    @Output() bulkAssignStaff = new EventEmitter<any>();

    // ──────────────────────────────────────────────────────────────
    //  Outputs — Post actions
    // ──────────────────────────────────────────────────────────────

    @Output() addSiblingPost = new EventEmitter<unknown>();
    @Output() editPost = new EventEmitter<unknown>();
    @Output() deletePost = new EventEmitter<unknown>();

    // ──────────────────────────────────────────────────────────────
    //  Outputs — User & navigation
    // ──────────────────────────────────────────────────────────────

    /** Emitted when removing a user from a position (edit mode) */
    @Output() removeUserFromPosition = new EventEmitter<{ userId: string; partKey: string }>();

    /** Emitted when clicking a position name — opens position details sidebar */
    @Output() openPositionTemplate = new EventEmitter<any>();

    /** Emitted when clicking "Approve" on a pending user — opens approval dialog */
    @Output() approveUser = new EventEmitter<{ user: any; node: ECONode<Position>; post?: any }>();

    /**
     * Emitted when clicking the HFA (Held From Above) arrow_circle_down icon.
     * Emits the post's heldFromAbovePartKey — the partKey of the origin post
     * where the user is actually assigned.
     *
     * IMPORTANT: The target is a POST partKey, not a top-level ECO node.
     * Posts are sub-parts nested inside ECO nodes (postsInParent,
     * postsInSection, postsInUnit). The parent handler onNavigateToHfaOrigin()
     * searches all three levels to find which ECO node contains the target post,
     * then scrolls to that node and opens the sidebar.
     *
     * @see organization-board.component.ts → onNavigateToHfaOrigin()
     * @see organization-board.component.ts → _findPostInSubParts()
     */
    @Output() navigateToHfaOrigin = new EventEmitter<string>();

    /** Emitted when a VFP value is changed (node-level or sub-part level) */
    @Output() vfpChanged = new EventEmitter<{ partKey: string; vfp: string }>();

    /**
     * Emitted when the user pins/unpins a VFP via the pin icon in edit mode.
     * The host saves `vfpAlwaysVisible` on the part so the VFP stays visible
     * permanently on the read-only org board (no hover required).
     */
    @Output() vfpAlwaysVisibleChanged = new EventEmitter<{ partKey: string; vfpAlwaysVisible: boolean }>();

    // ──────────────────────────────────────────────────────────────
    //  VFP Editing State
    // ──────────────────────────────────────────────────────────────

    /** Whether the node-level VFP is currently being edited */
    public isEditingVfp = false;

    /** Tracks which sub-part VFPs (sections, units, posts) are being edited, keyed by partKey */
    public editingSubPartVfp: Record<string, boolean> = {};

    private _vfpCancelled = false;
    private _subPartVfpCancelled: Record<string, boolean> = {};

    // ──────────────────────────────────────────────────────────────
    //  CDK Drop List
    // ──────────────────────────────────────────────────────────────

    public dropListElement!: CdkDropList;

    @ViewChild(CdkDropList)
    public set dropListChanged(value: CdkDropList) {
        if (value) {
            this.dropListElement = value;
            this.dropList.emit(value);
        }
    }

    // ──────────────────────────────────────────────────────────────
    //  Computed Properties
    // ──────────────────────────────────────────────────────────────

    /** Check if a specific part is being deleted */
    public isDeleting(partKey: string): boolean {
        return this.deletingPartKeys.has(partKey);
    }

    /** Check if a specific user on a specific part is being processed (removal/approval) */
    public isProcessingUser(userId: string, partKey: string): boolean {
        return this.processingUserKeys.has(`${userId}:${partKey}`);
    }

    /**
     * Returns the user details array for this node's ECO-level display.
     * Also pre-loads profile colors for all users (used for avatar generation).
     */
    public get userDetailArray() {
        const arr = (<any>this.node).data.userDetailsArray;
        if (arr?.length) {
            this.profileColorService.ensureLoadedMany(arr.map((u: any) => u?.uid || u?.key).filter(Boolean));
        }
        return arr;
    }

    /** Returns only active (non-pending) users — used in view mode */
    public get activeUserDetailArray() {
        const arr = this.userDetailArray;
        if (!arr?.length) return arr;
        return arr.filter((u: any) => !u?.isPending);
    }

    /** Returns the display array based on edit mode: all users in edit, only active in view */
    public get displayUserDetailArray() {
        return this.isEditing ? this.userDetailArray : this.activeUserDetailArray;
    }

    /** How many users to show based on edit/view mode */
    public get displayUserCount(): number {
        return this.displayUserDetailArray?.length || 0;
    }

    /** Recalculates user display mode for view mode (hides pending users) */
    public get effectiveUserDisplayMode(): 'NO_USERS' | 'ONE_USER' | 'MULTIPLE_USERS' {
        if (this.isEditing) return this.userDisplayMode;
        // In view mode, recalculate based on active users only
        const count = this.activeUserDetailArray?.length || 0;
        if (count === 0) return 'NO_USERS';
        if (count === 1) return 'ONE_USER';
        return 'MULTIPLE_USERS';
    }

    /**
     * Returns visible users for a sub-part post based on edit/view mode.
     * In edit mode: all users (active + pending). In view mode: only active users.
     */
    public getVisibleUsersForPost(post: any): any[] {
        if (!post?.userDetailsArray?.length) return [];
        if (this.isEditing) return post.userDetailsArray;
        return post.userDetailsArray.filter((u: any) => !u?.isPending);
    }

    /**
     * Returns true if a sub-part post has at least one active (non-pending) user.
     * Used to decide whether to show HFA — HFA only shows when there are no active AND no pending users.
     */
    public hasActiveUsersOnPost(post: any): boolean {
        if (!post?.userDetailsArray?.length) return false;
        return post.userDetailsArray.some((u: any) => !u?.isPending);
    }

    /**
     * Returns true if the post has any pending (not yet approved) users.
     * Used to suppress HFA display — a post with pending users is "occupied" (pending review).
     */
    public hasPendingUsersOnPost(post: any): boolean {
        if (!post?.userDetailsArray?.length) return false;
        return post.userDetailsArray.some((u: any) => u?.isPending);
    }

    /** Typed escape hatch — gives template access to node properties beyond the strict type */
    public get nodeAsAny() {
        return <any>this.node;
    }

    /**
     * Returns HFA (Held From Above) users for the ECO-level node.
     * Resolves the comma-separated UID string into OrgUser objects.
     */
    public get hfaAsUsers() {
        const hfaUidString = this.nodeAsAny?.data?.heldFromAboveUid;
        if (!hfaUidString || this.org === null) {
            return [];
        }
        return hfaUidString
            .split(',')
            .map((uid: string) => (this.org as Organization).orgUsers[uid])
            .filter((user: OrgUser | undefined): user is OrgUser => user !== undefined);
    }

    // ──────────────────────────────────────────────────────────────
    //  HFA (Held From Above) Resolution
    //
    //  HFA is the system by which an unstaffed post inherits users from
    //  a parent position. The post stores:
    //    - heldFromAboveUid: comma-separated string of inherited user UIDs
    //    - heldFromAbovePartKey: partKey of the origin post where users are assigned
    //    - heldFromAboveColor: color of the origin node for the HFA icon
    //
    //  Two getters exist for different levels:
    //    - hfaAsUsers (getter): ECO-level HFA from node.data.heldFromAboveUid
    //    - hfaAsUsers2(node): Sub-part-level HFA from a post object
    //
    //  The template shows HFA users only when the post has no own userDetailsArray.
    //  Clicking the arrow_circle_down icon emits navigateToHfaOrigin with the
    //  heldFromAbovePartKey to navigate to the origin position.
    // ──────────────────────────────────────────────────────────────

    /**
     * Returns HFA users for a specific sub-part (post within section/unit).
     * Used when a post has no directly assigned users but inherits from a parent position.
     *
     * The heldFromAboveUid is a comma-separated string of UIDs that gets split
     * and resolved against org.orgUsers to get full OrgUser objects for display.
     *
     * @param node - The sub-part data (e.g., a post object from postsInParent/postsInSection/postsInUnit)
     * @returns Array of OrgUser objects resolved from the HFA UID string
     */
    public hfaAsUsers2(node: any): OrgUser[] {
        const hfaUidString = node?.heldFromAboveUid;
        if (!hfaUidString || this.org === null) {
            return [];
        }
        return hfaUidString
            .split(',')
            .map((uid: string) => (this.org as Organization).orgUsers[uid])
            .filter((user: OrgUser | undefined): user is OrgUser => user !== undefined);
    }

    /**
     * Whether the ECO-level HFA is from pending (unapproved) users.
     * In view mode, pending HFA should be hidden. In edit mode, shown with indicator.
     */
    public get isHfaPending(): boolean {
        return !!this.nodeAsAny?.data?.heldFromAboveIsPending;
    }

    /**
     * Whether a sub-part's HFA is from pending users.
     */
    public isHfaPending2(post: any): boolean {
        return !!post?.heldFromAboveIsPending;
    }

    /**
     * Whether to show ECO-level HFA.
     * In view mode: always show (pending users are hidden).
     * In edit mode: hide HFA when post has pending users (shown directly).
     */
    public get shouldShowHfa(): boolean {
        if (!this.hfaAsUsers?.length) return false;
        if (!this.isEditing) return true; // View mode: always show
        // Edit mode: only if no pending users on this node
        const pendingUsers = this.nodeAsAny?.data?.userDetailsArray?.filter((u: any) => u?.isPending) || [];
        return pendingUsers.length === 0;
    }

    /**
     * Whether to show sub-part HFA.
     * In view mode: always show HFA (pending users are hidden, so post appears vacant).
     * In edit mode: hide normal HFA when the post has pending HFA users (show those instead),
     *               show normal HFA only on truly vacant posts without pending HFA.
     */
    public shouldShowHfa2(post: any): boolean {
        if (!this.isEditing) {
            // View mode: show normal HFA if available
            const users = this.hfaAsUsers2(post);
            return !!users?.length;
        }
        // Edit mode: hide normal HFA only if post has pending users shown directly
        if (this.hasPendingUsersOnPost(post)) return false;
        // Show normal HFA alongside pending HFA (both can appear together)
        const users = this.hfaAsUsers2(post);
        return !!users?.length;
    }

    /**
     * Whether to show pending HFA on a sub-part post (edit mode only).
     * Shows Danny/Carl as pending HFA instead of Jessica when their pending assignment
     * is on the nearest ancestor post.
     */
    public shouldShowPendingHfa2(post: any): boolean {
        if (!this.isEditing) return false;
        if (this.hasPendingUsersOnPost(post)) return false; // Don't show pending HFA on the post where they're directly assigned
        return post?.pendingHfaUserDetailsArray?.length > 0;
    }

    /**
     * Get the pending HFA users for a sub-part post.
     */
    public getPendingHfaUsers(post: any): any[] {
        return post?.pendingHfaUserDetailsArray || [];
    }

    // ──────────────────────────────────────────────────────────────
    //  Avatar URL Resolution
    // ──────────────────────────────────────────────────────────────

    /**
     * Gets the avatar URL for a user, with fallback chain:
     *   1. User's photoThumbnail property
     *   2. Organization's orgUsers photoThumbnail (most up-to-date)
     *   3. Generated avatar from ProfileColorService (initials + color)
     */
    public isUserOnline(user: any): boolean {
        const uid = user?.uid || user?.key;
        return !!uid && this.dialogService.onlineUserIds?.includes(uid);
    }

    public getUserAvatarUrl(user: OrgUser | any): string {
        if (user?.photoThumbnail) {
            return user.photoThumbnail;
        }
        const uid = user?.uid || user?.key;
        if (uid && this.org?.orgUsers?.[uid]?.photoThumbnail) {
            return this.org.orgUsers[uid].photoThumbnail || '';
        }
        const name = user?.name || 'User';
        return this.profileColorService.getAvatarUrl(uid || '', name);
    }

    /** Returns a solid light desaturated tint of a hex color, e.g. #0000FF → light muted blue.
     *  `mix` controls how much to lighten toward white (0 = original, 1 = white).
     *  Default 0.7 keeps the parent hue clearly visible for unit backgrounds. */
    public getLightTint(hex: string | undefined, mix = 0.7): string | null {
        if (!hex || !hex.startsWith('#') || hex.length < 7) return null;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        // Desaturate: blend toward grey first (50% desaturation)
        const grey = Math.round(r * 0.299 + g * 0.587 + b * 0.114);
        const desat = 0.5;
        const dr = Math.round(r + (grey - r) * desat);
        const dg = Math.round(g + (grey - g) * desat);
        const db = Math.round(b + (grey - b) * desat);
        // Then lighten toward white
        const tr = Math.round(dr + (255 - dr) * mix);
        const tg = Math.round(dg + (255 - dg) * mix);
        const tb = Math.round(db + (255 - db) * mix);
        return `rgb(${tr}, ${tg}, ${tb})`;
    }

    // ──────────────────────────────────────────────────────────────
    //  Section Action Handlers
    // ──────────────────────────────────────────────────────────────

    public onEditSection(section: any): void {
        this.editSection.emit(section);
    }

    public onAddSibling(section: any): void {
        this.addSiblingSection.emit(section);
    }

    public onAddChild(section: any): void {
        this.addChildUnit.emit(section);
    }

    public onDeleteSection(section: any): void {
        this.deleteSection.emit(section);
    }

    public onReorderSectionChildren(section: any): void {
        this.reorderSectionChildren.emit(section);
    }

    // ──────────────────────────────────────────────────────────────
    //  Unit Action Handlers
    // ──────────────────────────────────────────────────────────────

    public onEditUnit(unit: unknown): void {
        this.editUnit.emit(unit);
    }

    public onAddSiblingUnit(unit: unknown): void {
        this.addSiblingUnit.emit(unit);
    }

    public onAddChildPost(unit: unknown): void {
        this.addChildPost.emit(unit);
    }

    public onDeleteUnit(unit: unknown): void {
        this.deleteUnit.emit(unit);
    }

    public onReorderUnitChildren(unit: any): void {
        this.reorderUnitChildren.emit(unit);
    }

    // ──────────────────────────────────────────────────────────────
    //  Post Action Handlers
    // ──────────────────────────────────────────────────────────────

    public onAddSiblingPost(post: unknown): void {
        this.addSiblingPost.emit(post);
    }

    public onEditPost(post: unknown): void {
        this.editPost.emit(post);
    }

    public onDeletePost(post: unknown): void {
        this.deletePost.emit(post);
    }

    // ──────────────────────────────────────────────────────────────
    //  User Removal
    // ──────────────────────────────────────────────────────────────

    /**
     * Handles removing a user from a position.
     * Guards against missing user key or partKey to prevent invalid operations.
     */
    public onRemoveUser(user: any, partKey: string | undefined): void {
        if (!user?.key || !partKey) {
            console.warn('[org-board-node] Cannot remove user - missing user key or partKey', { user, partKey });
            return;
        }
        this.removeUserFromPosition.emit({ userId: user.key, partKey });
    }

    // ──────────────────────────────────────────────────────────────
    //  Node-Level VFP Editing
    //
    //  VFP = Valuable Final Product (the core deliverable of a position).
    //  Supports inline editing at the bottom of the card.
    // ──────────────────────────────────────────────────────────────

    public startEditingVfp(): void {
        if (!this.isEditing || this.isEditingVfp) return;
        this.isEditingVfp = true;
        setTimeout(() => {
            const textarea = this.elRef.nativeElement.querySelector('.vfp-section textarea') as HTMLTextAreaElement;
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        });
    }

    public cancelEditingVfp(): void {
        this._vfpCancelled = true;
        this.isEditingVfp = false;
    }

    public saveVfp(value: string): void {
        this.isEditingVfp = false;
        const partKey = this.nodeAsAny?.data?.partKey || this.node?.data?.key || '';
        // Optimistically update local data so the view shows text immediately
        if (this.nodeAsAny?.data) {
            this.nodeAsAny.data.vfp = value;
        }
        if (partKey) {
            this.vfpChanged.emit({ partKey, vfp: value });
        }
    }

    public onVfpBlur(value: string): void {
        if (this._vfpCancelled) {
            this._vfpCancelled = false;
            return;
        }
        this.saveVfp(value);
    }

    public onVfpKeydown(event: KeyboardEvent, textarea: HTMLTextAreaElement): void {
        if (event.key === 'Escape') {
            event.stopPropagation();
            event.preventDefault();
            this.cancelEditingVfp();
        } else if (event.key === 'Enter' && event.ctrlKey) {
            event.stopPropagation();
            event.preventDefault();
            this.saveVfp(textarea.value);
        }
    }

    /**
     * Toggles the per-VFP `vfpAlwaysVisible` pin. When pinned, the VFP renders
     * permanently on the read-only org board instead of being hover-only.
     *
     * @param partKey  partKey of the part owning the VFP (node-level or sub-part)
     * @param dataRef  the part data object (mutated optimistically so the UI updates instantly)
     * @param event    optional click event whose propagation we suppress (so the click
     *                 doesn't also trigger the VFP edit textarea)
     */
    public toggleVfpAlwaysVisible(partKey: string, dataRef: any, event?: Event): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        if (!partKey || !dataRef) return;
        const next = !dataRef.vfpAlwaysVisible;
        // Optimistic local update — keeps the toggle responsive even before the
        // backend round-trip completes.
        dataRef.vfpAlwaysVisible = next;
        this.vfpAlwaysVisibleChanged.emit({ partKey, vfpAlwaysVisible: next });
    }

    // ──────────────────────────────────────────────────────────────
    //  Sub-Part VFP Editing (Sections, Units, Posts within cards)
    //
    //  Each sub-part has its own VFP, tracked by partKey.
    //  This allows editing VFPs at any level of the hierarchy:
    //    Section VFP, Unit VFP, Post VFP
    // ──────────────────────────────────────────────────────────────

    public startEditingSubPartVfp(partKey: string): void {
        if (!this.isEditing || this.editingSubPartVfp[partKey]) return;
        this.editingSubPartVfp[partKey] = true;
        setTimeout(() => {
            const textarea = this.elRef.nativeElement.querySelector('.sub-part-vfp textarea') as HTMLTextAreaElement;
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        });
    }

    public saveSubPartVfp(partKey: string, value: string, part: any): void {
        this.editingSubPartVfp[partKey] = false;
        if (part) part.vfp = value;
        if (partKey) this.vfpChanged.emit({ partKey, vfp: value });
    }

    public cancelEditingSubPartVfp(partKey: string): void {
        this._subPartVfpCancelled[partKey] = true;
        this.editingSubPartVfp[partKey] = false;
    }

    public onSubPartVfpBlur(partKey: string, value: string, part: any): void {
        if (this._subPartVfpCancelled[partKey]) {
            this._subPartVfpCancelled[partKey] = false;
            return;
        }
        this.saveSubPartVfp(partKey, value, part);
    }

    public onSubPartVfpKeydown(event: KeyboardEvent, partKey: string, textarea: HTMLTextAreaElement, part: any): void {
        if (event.key === 'Escape') {
            event.stopPropagation();
            event.preventDefault();
            this.cancelEditingSubPartVfp(partKey);
        } else if (event.key === 'Enter' && event.ctrlKey) {
            event.stopPropagation();
            event.preventDefault();
            this.saveSubPartVfp(partKey, textarea.value, part);
        }
    }
}
